import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { X, Mail, AlertTriangle } from 'lucide-react';
import { apiService } from '@/services/api';
import { useToast } from '@/hooks/use-toast';

type Invoice = {
  id: number;
  invoice_number: string;
  customer_name?: string;
  customer_email?: string;
  total: number;
  due_date?: string;
  days_overdue: number;
  penalty_amount: number;
};

type SendReminderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onSuccess?: () => void;
};

const REMINDER_TEMPLATES = {
  auto_1: {
    label: 'Relance automatique 1 (J+7)',
    subject: 'Rappel: Facture {invoice_number} échue',
    message: `Bonjour,

Nous vous informons que la facture {invoice_number} d'un montant de {total} € est échue depuis le {due_date}.

Nous vous remercions de bien vouloir procéder au règlement dans les meilleurs délais.

Cordialement,`
  },
  auto_2: {
    label: 'Relance automatique 2 (J+15)',
    subject: '2ème rappel: Facture {invoice_number} en retard',
    message: `Bonjour,

Malgré notre précédent rappel, nous constatons que la facture {invoice_number} d'un montant de {total} € n'a toujours pas été réglée.

Cette facture est en retard de {days_overdue} jours.

Nous vous prions de bien vouloir régulariser votre situation rapidement.

Cordialement,`
  },
  formal: {
    label: 'Relance formelle (J+30)',
    subject: 'Mise en demeure: Facture {invoice_number}',
    message: `Madame, Monsieur,

Par la présente, nous vous mettons formellement en demeure de régler la facture {invoice_number} d'un montant de {total} €, échue depuis {days_overdue} jours.

À défaut de règlement sous 8 jours, nous nous verrons contraints d'engager une procédure de recouvrement contentieux.

Des pénalités de retard d'un montant de {penalty_amount} € ont été appliquées conformément aux conditions générales de vente.

Cordialement,`
  },
  manual: {
    label: 'Relance manuelle personnalisée',
    subject: 'Rappel: Facture {invoice_number}',
    message: `Bonjour,

Nous vous contactons concernant la facture {invoice_number}.

Cordialement,`
  }
};

export const SendReminderModal: React.FC<SendReminderModalProps> = ({ isOpen, onClose, invoice, onSuccess }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [reminderType, setReminderType] = useState<keyof typeof REMINDER_TEMPLATES>('manual');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    if (invoice && isOpen) {
      const template = REMINDER_TEMPLATES[reminderType];
      setSubject(
        template.subject
          .replace('{invoice_number}', invoice.invoice_number)
          .replace('{total}', invoice.total.toFixed(2))
      );
      setMessage(
        template.message
          .replace('{invoice_number}', invoice.invoice_number)
          .replace('{total}', invoice.total.toFixed(2))
          .replace('{due_date}', invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('fr-FR') : '—')
          .replace('{days_overdue}', invoice.days_overdue.toString())
          .replace('{penalty_amount}', invoice.penalty_amount.toFixed(2))
      );
    }
  }, [invoice, reminderType, isOpen]);

  if (!isOpen || !invoice) return null;

  const handleSend = async () => {
    if (!invoice.customer_email) {
      toast({ title: 'Erreur', description: 'Aucun email client défini.', variant: 'destructive' });
      return;
    }

    try {
      setBusy(true);
      await apiService.post(`/invoices/${invoice.id}/send-reminder`, {
        reminder_type: reminderType,
        subject,
        message,
        notes
      });
      
      toast({ 
        title: 'Relance envoyée', 
        description: `La relance a été envoyée à ${invoice.customer_email}` 
      });
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (e: any) {
      toast({ 
        title: 'Erreur', 
        description: e.response?.data?.detail || 'Impossible d\'envoyer la relance.', 
        variant: 'destructive' 
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 dark:border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Mail className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Envoyer une relance</h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Facture {invoice.invoice_number} • {invoice.customer_name}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-auto">
          {/* Warning */}
          {invoice.days_overdue > 0 && (
            <div className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/30 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="font-semibold text-orange-900 dark:text-orange-300">Facture en retard</p>
                <p className="text-orange-700 dark:text-orange-400">
                  {invoice.days_overdue} jour{invoice.days_overdue > 1 ? 's' : ''} de retard
                  {invoice.penalty_amount > 0 && ` • Pénalités: ${invoice.penalty_amount.toFixed(2)} €`}
                </p>
              </div>
            </div>
          )}

          {/* Email destinataire */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Destinataire
            </label>
            <input
              type="email"
              value={invoice.customer_email || ''}
              disabled
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Type de relance */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Type de relance
            </label>
            <select
              value={reminderType}
              onChange={(e) => setReminderType(e.target.value as keyof typeof REMINDER_TEMPLATES)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            >
              {Object.entries(REMINDER_TEMPLATES).map(([key, template]) => (
                <option key={key} value={key}>{template.label}</option>
              ))}
            </select>
          </div>

          {/* Sujet */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Sujet
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-mono text-sm"
            />
          </div>

          {/* Notes internes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              Notes internes (optionnel)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Notes visibles uniquement en interne..."
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button 
            onClick={handleSend} 
            disabled={busy || !invoice.customer_email}
            className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
          >
            <Mail className="h-4 w-4 mr-2" />
            {busy ? 'Envoi...' : 'Envoyer la relance'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendReminderModal;
